import clsx from 'clsx'
import { useEffect, useRef } from 'react'
import toast from 'react-hot-toast'
import { useToggle } from 'usehooks-ts'

import { FvButton, Icon } from '@fv/client-components'

import { CarrierTooltip } from '@/components/CarrierTooltip'
import { supportMessage } from '@/constants'
import type { Opportunity } from '@/types/Opportunity'
import { getBidExpiration } from '@/utils/getBidExpiration'

import {
  AdditionalTruckInfoForm,
  AdditionalTruckInfoToggle,
} from '../opp-details/AdditionalTruckInfoForm'
import {
  useOppListingActions,
  useOppListingState,
} from '../opportunities/OppListingContextProvider'
import {
  type OppListingStatus,
  type OppPage,
  oppPageStatusMap,
} from '../opportunities/types'
import { QuotesForm } from '../quote/QuotesForm'
import { QuotesFormProvider } from '../quote/QuotesFormProvider'
import { CardActions } from './CardActions'
import { ConfirmationForm } from './ConfirmationForm'
import { ExpirationNotice } from './ExpirationNotice'
import { useArchiveOpportunity } from './hooks/useArchiveOpportunity'
import { useRejectLoad } from './hooks/useRejectLoad'
import { shouldShowPricingIndicator } from './pricingIndicators'
import { ShipmentDetails } from './ShipmentDetails'
import { ShipperCanceledOverlay } from './ShipperCanceledOverlay'
import { TenderExpiration } from './TenderExpiration'

type Props = {
  isActive: boolean
  opportunity: Opportunity
}

const getPageFromStatus = (status: OppListingStatus): OppPage => {
  return Object.entries(oppPageStatusMap).find(([, statuses]) =>
    [...statuses].includes(status),
  )?.[0] as OppPage
}

const bidExpirationStatuses: OppListingStatus[] = ['offered', 'open']

export const OppCard = ({ isActive, opportunity }: Props) => {
  const { setActiveId } = useOppListingActions()
  const { page: storePage } = useOppListingState()
  const { bidExpirationDate, isExpired } = getBidExpiration(opportunity)
  const oppStatus = opportunity.status
  const [showQuoteForm, toggleQuoteForm, setShowQuoteForm] = useToggle()
  const [showTruckInfo, toggleTruckInfo] = useToggle()
  const ref = useRef<HTMLDivElement | null>(null)
  // 90% of the time page will not change depending on the status, so keep this in the store for cacheability
  const page =
    storePage === 'search' || storePage === 'direct'
      ? getPageFromStatus(oppStatus)
      : storePage

  const { isArchived, loadId } = opportunity
  const rejectLoad = useRejectLoad()
  const archive = useArchiveOpportunity()
  const archiveIcon = isArchived ? 'trash-restore-alt' : 'archive'
  function archiveQuoteRequest(shouldArchive: boolean) {
    if (archive.isPending || rejectLoad.isPending) return

    archive.mutateAsync({ archive: shouldArchive, loadId }).catch(() => {
      toast.error(`Unable to archive shipment, ${supportMessage}`)
    })
  }
  function rejectActiveLoad() {
    if (archive.isPending || rejectLoad.isPending) return

    if (
      window.confirm(
        "By clicking 'ok' you are acknowledging that you can no longer take this load even though you have already confirmed with the shipper. We will notify the shipper in Freightview and via email.",
      )
    ) {
      rejectLoad.mutateAsync({ loadId }).catch(() => {
        toast.error(`Unable to decline load, ${supportMessage}`)
      })
    }
  }
  useEffect(() => {
    if (showQuoteForm && !isActive) setShowQuoteForm(false)
  }, [isActive, showQuoteForm, setShowQuoteForm])

  const scrollIntoView = () => {
    setTimeout(() => {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 300)
  }

  const isLowestForIndicator = opportunity?.isLowest
  const showPricingIcon = shouldShowPricingIndicator(opportunity)
  const showCurrentLowestBid =
    !opportunity?.isLowest && !!opportunity?.lowestBid
  const showThirdPartyPill = opportunity.isFreightCollect
  const waterfallTender = opportunity.waterfallTender
  const headerPillClassName = 'border-2 flex rounded-full px-2 py-1 text-sm'
  const showHeaderRow =
    !opportunity.isCanceled &&
    (showPricingIcon ||
      showCurrentLowestBid ||
      showThirdPartyPill ||
      !!waterfallTender)

  return (
    <div
      className={clsx('shipment-item opacity-80', {
        'shipment-item--selected border-fv-orange-bright border-2 b1000:border-r !opacity-100 -ml-px after:w-6 after:h-6 after:translate-x-[calc(50%+1px)] after:right-0 after:bg-white after:border-l-0 after:border-b-0 after:absolute after:top-1/2 after:-translate-y-1/2 after:rotate-45 after:border-2 after:border-fv-orange-bright':
          isActive,
        'mt-10 pt-10': showHeaderRow,
        'pb-14': oppStatus === 'confirmed',
      })}
      ref={ref}
      style={{ scrollMarginTop: '2.5rem' }}
      onClick={() => {
        if (!isActive) {
          setActiveId(opportunity.loadId)
          scrollIntoView()
        }
      }}
    >
      {showHeaderRow && (
        <div
          className={clsx(
            'absolute -top-6 -left-3 z-[2000] flex items-center gap-2 mb-4  p-2 rounded-full bg-white',
            isActive
              ? 'border-fv-orange-bright border-2'
              : 'border-fv-gray border',
          )}
        >
          {showPricingIcon && (
            <div className="flex items-center">
              <div
                className={`flex relative z-10 items-center justify-center h-8 w-8 rounded-full border-2 bg-white ${isLowestForIndicator ? 'border-[#99d87a] !bg-[#e4f6dc]' : 'border-red-400 !bg-red-100'}`}
              >
                <Icon
                  className={`text-[1rem] ${isLowestForIndicator ? 'text-[#99d87a]' : 'text-red-400'}`}
                  icon={isLowestForIndicator ? 'arrow-down' : 'arrow-up'}
                />
              </div>

              <div
                className={clsx(
                  'text-inherit pr-3 pl-5 leading-none py-[.35rem] border rounded-full text-xs italic -ml-4',
                  {
                    'bg-[#e4f6dc] border-[#99d87a]': isLowestForIndicator,
                    'bg-red-100  border-red-400': !isLowestForIndicator,
                  },
                )}
              >
                {isLowestForIndicator
                  ? 'You have the lowest bid'
                  : 'You do not have the lowest bid'}
              </div>
            </div>
          )}
          {showCurrentLowestBid && (
            <div className="bg-red-100 text-inherit pr-2 pl-2 leading-none py-[.35rem] border border-red-300 rounded-full text-xs italic">
              Current lowest bid - $
              {(opportunity?.lowestBid ?? 0).toLocaleString(undefined, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}{' '}
              {(opportunity?.lowestBidCurrency ?? 'usd').toUpperCase()}
            </div>
          )}
          {showThirdPartyPill && (
            <div className={`bg-red-100 border-red-300 ${headerPillClassName}`}>
              Bill to third party
            </div>
          )}
          {!!waterfallTender && !opportunity.isCanceled && (
            <ExpirationNotice>
              <TenderExpiration expiresAt={waterfallTender.expiresAt} />
            </ExpirationNotice>
          )}
        </div>
      )}
      {bidExpirationStatuses.includes(oppStatus) && bidExpirationDate && (
        <ExpirationNotice>
          {isExpired ? 'Bidding expired on' : 'Bidding expires on '}{' '}
          {bidExpirationDate}
        </ExpirationNotice>
      )}
      <div className="shipment-item__initial relative">
        {page !== 'open' && <CardActions opportunity={opportunity} />}
        <ShipmentDetails opportunity={opportunity} />
        {page === 'open' && (
          <CardActions
            opportunity={opportunity}
            onToggleQuoteForm={toggleQuoteForm}
          />
        )}
      </div>
      {isActive && oppStatus === 'awarded' && (
        <ConfirmationForm opportunity={opportunity} />
      )}
      {oppStatus === 'confirmed' && (
        <div className="mt-6">
          <AdditionalTruckInfoToggle
            value={showTruckInfo}
            onToggle={toggleTruckInfo}
          />
        </div>
      )}
      {showTruckInfo && oppStatus !== 'awarded' && (
        <AdditionalTruckInfoForm
          opportunity={opportunity}
          className="mt-4 pt-4 border-fv-gray border-t border-dashed"
          onSubmit={toggleTruckInfo}
        />
      )}

      {(showQuoteForm ||
        (isActive && page === 'quoting' && oppStatus !== 'lost')) && (
        <QuotesFormProvider opportunity={opportunity}>
          <QuotesForm
            hasQuoted={
              page === 'quoting' || page === 'won' || page === 'active'
            }
          />
        </QuotesFormProvider>
      )}
      {page === 'active' && !showTruckInfo && !showQuoteForm && (
        <div className="absolute bottom-4 flex divide-x divide-fv-gray [&>*]:px-2 right-2">
          {!opportunity.isFreightCollect && (
            <CarrierTooltip label={'Manage charges'}>
              <FvButton onClick={toggleQuoteForm} icon="money-check-edit-alt">
                Manage charges
              </FvButton>
            </CarrierTooltip>
          )}
          <CarrierTooltip
            label={
              opportunity.isArchived ? 'Move back to active queue' : 'Archive'
            }
          >
            <FvButton
              className="m-0"
              onClick={() => archiveQuoteRequest(!isArchived)}
              icon={archiveIcon}
              loading={archive.isPending}
            />
          </CarrierTooltip>

          <CarrierTooltip label="Decline this load">
            <FvButton
              className="m-0"
              loading={rejectLoad.isPending}
              icon="ban"
              iconClass="text-fv-orange-bright"
              onClick={rejectActiveLoad}
            />
          </CarrierTooltip>
        </div>
      )}

      {opportunity.isCanceled && (
        <ShipperCanceledOverlay
          canArchive={!opportunity.isArchived}
          opportunity={opportunity}
        />
      )}
    </div>
  )
}
